// Офлайн-очередь записей: операции применяются к локальной Dexie-базе сразу
// (оптимистично), складываются в pendingOps и отправляются в Cloudflare Worker
// (POST /submit) при появлении сети. Владелец забирает их своим `npm run sync`,
// модерирует и публикует в снапшот — тогда их увидят остальные пользователи.

import {
  db,
  type PendingOp,
  getPendingOps,
  removePendingOp,
  addPendingOp,
  putAd,
  removeAd as removeAdFromDb,
  updateAdStatus as updateAdStatusInDb,
} from './database';
import { submitOp, SubmitError } from '../services/submit';
import { refreshSnapshot } from '../services/snapshot';
import { uuid } from '../services/uuid';
import type { Ad, AdCondition, AdStatus, AdContacts, Location } from '@barter/shared';
import type { SubmissionPayload } from '@barter/shared';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

const MAX_RETRIES = 10;

export interface CreateAdData {
  title: string;
  description: string;
  price?: number | null;
  currency?: string;
  categoryId?: string;
  condition?: AdCondition | null;
  wanted?: string | null;
  make?: string | null;
  model?: string | null;
  photos?: string[];
  contacts: AdContacts;
  location?: { city: string; district?: string } | null;
}

type SyncStatusListener = (status: SyncStatus) => void;

const listeners = new Set<SyncStatusListener>();
let currentStatus: SyncStatus = 'idle';
let processing = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const retryCount = new Map<string, number>();

export function getSyncStatus(): SyncStatus {
  return currentStatus;
}

export function onSyncStatusChange(listener: SyncStatusListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setStatus(status: SyncStatus) {
  currentStatus = status;
  listeners.forEach((l) => l(status));
}

function scheduleRetry(delay = 5000) {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => processQueue(), delay);
}

export async function enqueueCreate(data: CreateAdData, tempAd: Ad): Promise<void> {
  await putAd(tempAd);
  await addPendingOp({
    type: 'create',
    adId: tempAd.id,
    uuid: uuid(),
    timestamp: Date.now(),
    data: JSON.stringify(data),
  });
  scheduleRetry(0);
}

export async function enqueueUpdate(
  adId: string,
  data: Partial<CreateAdData> & { renew?: boolean },
): Promise<void> {
  // Оптимистично применяем правку к локальной копии
  const local = await db.ads.get(adId);
  if (local) {
    await putAd({
      ...local,
      ...(data.title !== undefined ? { title: data.title } : {}),
      ...(data.description !== undefined ? { description: data.description } : {}),
      ...(data.price !== undefined ? { price: data.price } : {}),
      ...(data.currency !== undefined ? { currency: data.currency as Ad['currency'] } : {}),
      ...(data.categoryId !== undefined ? { categoryId: data.categoryId } : {}),
      ...(data.condition !== undefined
        ? { condition: (data.condition ?? undefined) as Ad['condition'] }
        : {}),
      ...(data.wanted !== undefined ? { wanted: (data.wanted ?? undefined) as Ad['wanted'] } : {}),
      ...(data.make !== undefined ? { make: (data.make ?? undefined) as Ad['make'] } : {}),
      ...(data.model !== undefined ? { model: (data.model ?? undefined) as Ad['model'] } : {}),
      ...(data.photos !== undefined ? { photos: data.photos } : {}),
      ...(data.contacts !== undefined ? { contacts: data.contacts } : {}),
      ...(data.location !== undefined ? { location: data.location as Location | null } : {}),
      ...(data.renew ? { createdAt: Date.now() } : {}),
      updatedAt: Date.now(),
    });
  }
  await addPendingOp({
    type: 'update',
    adId,
    uuid: uuid(),
    timestamp: Date.now(),
    data: JSON.stringify(data),
  });
  scheduleRetry(0);
}

export async function enqueueDelete(adId: string): Promise<void> {
  await removeAdFromDb(adId);
  await addPendingOp({
    type: 'delete',
    adId,
    uuid: uuid(),
    timestamp: Date.now(),
  });
  scheduleRetry(0);
}

export async function enqueueStatusChange(adId: string, status: AdStatus): Promise<void> {
  await updateAdStatusInDb(adId, status);
  await addPendingOp({
    type: 'statusChange',
    adId,
    uuid: uuid(),
    timestamp: Date.now(),
    data: JSON.stringify({ status }),
  });
  scheduleRetry(0);
}

export async function enqueueReport(adId: string, reason: string): Promise<void> {
  await addPendingOp({
    type: 'report',
    adId,
    uuid: uuid(),
    timestamp: Date.now(),
    data: JSON.stringify({ reason }),
  });
  scheduleRetry(0);
}

function opToPayload(op: PendingOp): SubmissionPayload {
  const extra = op.data ? (JSON.parse(op.data) as Record<string, unknown>) : {};
  return { adId: op.adId, ...extra } as SubmissionPayload;
}

async function processOp(op: PendingOp): Promise<boolean> {
  try {
    await submitOp({
      uuid: op.uuid || `${op.type}-${op.adId}-${op.timestamp}`,
      kind: op.type,
      payload: opToPayload(op),
    });
    return true;
  } catch (err) {
    if (err instanceof SubmitError && (err.status === 400 || err.status === 403)) {
      // Отбраковано сервером (невалидно / заблокирован) — повторять бессмысленно.
      return true;
    }
    return false;
  }
}

export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export async function processQueue(): Promise<void> {
  if (processing) return;
  if (isOffline()) {
    setStatus('offline');
    return;
  }

  processing = true;
  setStatus('syncing');

  try {
    const ops = await getPendingOps();

    if (ops.length === 0) {
      setStatus('idle');
      return;
    }

    for (const op of ops) {
      const success = await processOp(op);
      if (success) {
        await removePendingOp(op.id!);
        retryCount.delete(op.adId);
      } else {
        const count = (retryCount.get(op.adId) || 0) + 1;
        retryCount.set(op.adId, count);
        if (count >= MAX_RETRIES) {
          console.warn(
            `[SyncQueue] Permanently failed ${op.type} for ad ${op.adId} after ${count} retries`,
          );
          window.dispatchEvent(
            new CustomEvent('barter:sync-failed', { detail: { adId: op.adId, type: op.type } }),
          );
          await removePendingOp(op.id!);
          retryCount.delete(op.adId);
          continue;
        }
        setStatus('error');
        scheduleRetry(10000);
        return;
      }
    }

    setStatus('idle');
  } catch {
    setStatus('error');
    scheduleRetry(10000);
  } finally {
    processing = false;
  }
}

/** Первичная синхронизация: скачать снапшот и отправить накопленные операции. */
export async function initialSync(): Promise<void> {
  if (isOffline()) {
    setStatus('offline');
    return;
  }

  setStatus('syncing');
  try {
    await refreshSnapshot();
    await processQueue();
  } catch {
    setStatus('error');
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setStatus('idle');
    processQueue();
  });
  window.addEventListener('offline', () => {
    setStatus('offline');
  });
}
